import { FingerPlacement } from './FingerPlacement';
// https://de.wikipedia.org/wiki/Akkordsymbol
interface BoardPosition {
  stringNr: number,
  fret: number,
};

export class ChordCalc {
  public static init: {[index: string] : (chord: string) => ChordCalc } = {
    'guitar': (chord: string) => new ChordCalc(chord, ['E', 'A', 'D', 'G', 'B', 'E']),
    'ukulele': (chord: string) => new ChordCalc(chord, ['G', 'C', 'E', 'A']),
  }

  private static readonly tunes: string[] = [
    'A', 'A#',
    'B',
    'C', 'C#',
    'D', 'D#',
    'E',
    'F', 'F#',
    'G', 'G#',
  ];

  private static readonly susMap: { [index:string]: number[]} = {
    "sus2": [2],
    "sus4": [5],
    "5": [12],
  }
  private static readonly addMap: { [index:string]: number[]} = {
    "6/9": [9, 13],
    "6": [9],
    "7": [10],
    "maj7": [11],
    "8": [12],
    "9": [10, 13],
    "add9": [13],
    "maj9": [11, 13],
    "10": [15],
    "11": [10, 13, 16],
    "12": [18],
    "13": [10, 13, 20],
  }

  public level: number = 0;
  protected toneBase: string = "C";
  protected toneMinor: boolean = false;
  protected toneSus: number[] = [];
  protected toneAdd: number[] = [];

  constructor(private _chord: string, private readonly strings: string[]) {
    this.chord = _chord;
  }

  public get chord(): string {
    return this._chord;
  }

  public set chord(v: string) {
    v = v.replace("_", "/");
    this._chord = v;

    // Base tone
    let end = 1;
    let additions = ['#'];
    while (additions.indexOf(v.charAt(end)) >= 0) {
      end++;
    }
    this.toneBase = v.substr(0, end);
    v = v.substr(end);
    // Minor/Major
    this.toneMinor = false;
    if (v.charAt(0) === 'm' && (v.charAt(1) !== 'i' && v.charAt(1) !== 'a')) {
      this.toneMinor = true;
      v = v.substr(1);
    }

    //short add like C5, C7, C9, Cmaj7
    let found = true;
    while(found) {
      found = false;
      Object.keys(ChordCalc.addMap).forEach((key: string) => {
        if (v.startsWith(key)) {
          found = true;
          console.info("found add", key, this._chord);
          ChordCalc.addMap[key].forEach((add: number) => this.toneAdd.push(add));
          v = v.substr(key.length);
          if (v.startsWith("/")) {
            v = v.substring(1);
          }
        }
      });
      Object.keys(ChordCalc.susMap).forEach((key: string) => {
        if (v.startsWith(key)) {
          found = true;
          ChordCalc.susMap[key].forEach((add: number) => this.toneSus.push(add));
          v = v.substr(key.length);
          if (v.startsWith("/")) {
            v = v.substring(1);
          }
        }
      });
    }

  }

  private getTone(nr: number = 0): string {
    let offset = 0;
    let start = (ChordCalc.tunes.indexOf(this.toneBase) + nr) % ChordCalc.tunes.length;
    if (this.level > 0) { // TODO: not done yet
      offset = (this.level * 2) % ChordCalc.tunes.length
      const jumpIndex = ['B', 'E'];
      // offset -= jumpIndex.indexOf(ChordCalc.tunes[ChordCalc.tunes.indexOf(this.toneBase)]) >= 0 ? -1 : 0;
      for (let i = 0; i < offset; i++) {
        const noteIndex = (start+i) % ChordCalc.tunes.length;
        const noteName = ChordCalc.tunes[noteIndex];
        const reduce = jumpIndex.indexOf(noteName) >= 0;
        // const log = reduce ? console.error : console.warn;

        // log(this.toneBase, ChordCalc.tunes.indexOf(this.toneBase), nr, ChordCalc.tunes[start],
        //   noteIndex, noteName, reduce,
        //   start, offset, ChordCalc.tunes[(start + offset)% ChordCalc.tunes.length]);

        offset += reduce ? -1 : 0;
      }
      // console.error((start + offset) % ChordCalc.tunes.length, ChordCalc.tunes[(start + offset)% ChordCalc.tunes.length]);
    }

    return ChordCalc.tunes[(start + offset) % ChordCalc.tunes.length];
  }

  public getToneIndex(tone: string): number {
    let tmp: string[] = [];
    for(let i=0; i < ChordCalc.tunes.length; i++) {
      tmp.push(this.getTone(i));
    }
    return tmp.indexOf(tone);
  }

  public getToneCaption(string: number, fret: number): string {
    const index = ((ChordCalc.tunes.indexOf(this.strings[string]) + fret) % ChordCalc.tunes.length);
    return ChordCalc.tunes[index];
  }

  public getToneSecunde(minor: boolean = false): string {
    return this.getTone(2 - (minor ? 1 : 0));
  }

  public getToneTerz(minor: boolean = false): string {
    return this.getTone(4 - (minor ? 1 : 0));
  }

  public getToneQuerz(minor: boolean = false): string {
    return this.getTone(5 - (minor ? 1 : 0));
  }

  public getToneQuinte(minor: boolean = false): string {
    return this.getTone(7 - (minor ? 1 : 0));
  }
  public getToneSexte(minor: boolean = false): string {
    return this.getTone(9 - (minor ? 0 : 1));
  }
  public getToneSeptime(minor: boolean = false): string {
    return this.getTone(10 + (minor ? 0 : 1));
  }

  public get signature(): string[] {
    let sus = this.toneSus.length ? this.toneSus : [this.toneMinor ? 3 : 4];
    let signature = [
      this.getTone(0),
      ...sus.map((sus: number) => this.getTone(sus)),
      this.getToneQuinte(),
      ...this.toneAdd.map((add: number) => this.getTone(add)),
    ];

    return signature.filter((v,i,s) => s.indexOf(v) === i);
  }

  public get all_positions(): { [index: string]: BoardPosition[]} {
    let positions: { [index: string]: BoardPosition[]} = {};
    this.signature.forEach((tone: string) => {
      positions[tone] = [];
      for(let stringNr=0; stringNr < this.strings.length; stringNr++) {
        let stringBase = this.strings[stringNr];
        for (let fret=0; fret<=4; fret++) {
          let index = (ChordCalc.tunes.indexOf(stringBase) + fret) % ChordCalc.tunes.length;
          let currentTone = ChordCalc.tunes[index];
          if (currentTone === tone) {
            positions[tone].push({
              stringNr,
              fret,
            });
          }
        }
      }
    });
    return positions;
  }

  public get positions(): number[] {
    let positions: number[] = new Array<number>(this.strings.length).fill(-1);
    // Add complete signature at least once
    this.signature.forEach((tone: string) => {
      let added = false;
      this.all_positions[tone].forEach((tonePos: BoardPosition) => {
        if (!added && positions[tonePos.stringNr] < 0) {
          positions[tonePos.stringNr] = tonePos.fret;
          added=true;
        }
      });
    });

    // Fill unused strings
    this.signature.forEach((tone: string) => {
      this.all_positions[tone].forEach((tonePos: BoardPosition) => {
        if (positions[tonePos.stringNr] < 0) {
          positions[tonePos.stringNr] = tonePos.fret;
        }
      });
    });

    return positions;
  }

  public get fingerPlacement(): FingerPlacement[] {
    let placement: FingerPlacement[] = [];
    let positions: number[] = this.positions;
    let signature: string[] = this.signature;
    for (let str=0; str<positions.length; str++) {
      let fret = positions[str];
      let note = this.getToneCaption(str, fret);
      placement.push({
        finger: 0,
        string: str,
        fret,
        note,
        tone: this.getToneIndex(note),
      });
    }

    return placement;
  }

  public get debug(): string {
    return JSON.stringify({
      chord: this.chord,
      base: this.toneBase,
      signature: this.signature,
      all_positions: this.all_positions,
      positions: this.positions,
      fingerplacement: this.fingerPlacement,
    }, null, ' ');
  }
}
